import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_PROPERTY } from '@kouto/types';
import { analyticsManager } from 'features/analytics';
import { useRecommendationsViewEvent } from 'features/analytics/hooks/useRecommendationsViewEvent';
import {
  FEATURED_FALLBACK_LIST_NAME,
  RECOMMENDATIONS_LIST_NAME,
} from 'features/analytics/utils/recommendationAnalytics';
import { useBrandId } from 'AppProvider';
import useCustomHistory from 'hooks/use-custom-history';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import useGetListingRecommendations from 'api/listingRecommendation/useGetListingRecommendations';
import useFeaturedListingsV3 from 'api/listing/useFeaturedListingsV3/useFeaturedListingsV3';
import useListingsV3 from 'api/listing/useListingsV3/useListingsV3';
import { GetListingRecommendationsResponse } from 'api/listingRecommendation/getListingRecommendations';
import { FeaturedListingV3 } from 'types/featuredListingV3';
import { BookingConfirmationCard } from 'components/molecules/BookingConfirmationCard';
import { BookingConfirmationCardSkeleton } from 'components/molecules/BookingConfirmationCard/BookingConfirmationCardSkeleton';
import { RecommendationCardGrid } from 'components/molecules/RecommendationCardGrid';
import { RecommendationsHeading } from 'components/molecules/RecommendationsHeading';
import { Button } from 'components/theme/Button';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageTitle } from 'components/atoms/PageTitle';

const PageWrapper = styled.div``;

const NavWrapper = styled.div`
  box-sizing: border-box;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 12px 24px 0px 24px;
`;

const HeaderSpacer = styled.div`
  padding-top: 48px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.font.primaryFont};
  gap: 30px;
  padding: 0 24px 24px 24px;
`;

const LinkBackButton = styled(Button)`
  border: none;
  outline: none;
  margin-top: 30px;
  width: 100%;
  max-width: 1200px;
`;

const Recommendations = () => {
  const { t: translate } = useTranslation();
  const { push: customPush } = useCustomHistory();
  const brandId = useBrandId();
  const languageCode = useSupportedLanguage();

  const recommendationIdRef = useRef(
    window.sessionStorage.getItem('recommendation_id'),
  );

  const { listings } = useListingsV3({
    limit: 3,
    language: languageCode,
    isUnlisted: false,
  });

  const { data: featuredListings, isLoading: isFeaturedListingsLoading } =
    useFeaturedListingsV3({ language: languageCode });

  const {
    listings: recommendedListings,
    isCompleted,
    isFailed,
    isPolling,
  } = useGetListingRecommendations({
    recommendationId: recommendationIdRef.current,
    maxPolls: 15,
    pollInterval: 2000,
    language: languageCode,
  });

  const hasRecommendations = isCompleted && recommendedListings.length > 0;
  const hasFeaturedListings = !!(
    featuredListings &&
    !isFeaturedListingsLoading &&
    featuredListings.length > 0
  );
  const shouldShowFeaturedListings =
    !hasRecommendations && hasFeaturedListings && (isFailed || isCompleted);
  const shouldShowRegularListings =
    !hasRecommendations && !hasFeaturedListings && (isFailed || isCompleted);

  useEffect(() => {
    analyticsManager.setAppData({
      [ANALYTICS_PROPERTY.PageType]: 'Recommendations page',
    });
  }, []);

  useRecommendationsViewEvent({
    listings: recommendedListings,
    isShown: hasRecommendations,
  });

  const getDisplayListings = (): FeaturedListingV3[] => {
    if (hasRecommendations) {
      return recommendedListings.map(
        (listing: GetListingRecommendationsResponse['listings'][number]) => ({
          ...listing,
          isUnlisted: listing.isUnlisted ?? false,
          category: listing.category
            ? { ...listing.category, brandId: brandId || '' }
            : null,
          vibes: listing.vibes.map((vibe) => ({
            ...vibe,
            brandId: brandId || '',
          })),
        }),
      );
    }

    if (shouldShowFeaturedListings) {
      return featuredListings ?? [];
    }

    if (shouldShowRegularListings) {
      return (
        listings?.map((listing) => ({ ...listing, isUnlisted: false })) ?? []
      );
    }

    return [];
  };

  const displayListings = getDisplayListings();

  const title = translate('similarExperiencesAvailableToBookDirectly');

  const onViewAllListings = () => {
    customPush({ pathname: '/e/' });
  };

  return (
    <PageWrapper>
      <PageTitle title="similarExperiencesAvailableToBookDirectly" />
      <NavWrapper>
        <NavigationHeader />
      </NavWrapper>
      <HeaderSpacer />
      <Section>
        <RecommendationsHeading title={title} />
        <RecommendationCardGrid>
          {isPolling ? (
            <>
              <BookingConfirmationCardSkeleton />
              <BookingConfirmationCardSkeleton />
              <BookingConfirmationCardSkeleton />
            </>
          ) : (
            displayListings.map((listing, index) => (
              <BookingConfirmationCard
                key={listing.id}
                listing={listing}
                recommendationId={recommendationIdRef.current}
                index={index}
                analyticsListName={
                  hasRecommendations
                    ? RECOMMENDATIONS_LIST_NAME
                    : FEATURED_FALLBACK_LIST_NAME
                }
              />
            ))
          )}
        </RecommendationCardGrid>
        <LinkBackButton onClick={onViewAllListings}>
          {translate('viewAll')}
        </LinkBackButton>
      </Section>
    </PageWrapper>
  );
};

export default Recommendations;
